// frontend/src/components/shared/Sidebar.tsx

import { LogOut, PanelLeftClose, PanelLeftOpen, Settings, User as UserIcon } from 'lucide-react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { NavBadge } from '@/config/nav';
import { useApp } from '@/context/AppContext';
import { useAuth } from '@/context/AuthContext';
import { useSidebar } from '@/context/SidebarContext';
import { usePermissions } from '@/hooks/usePermissions';
import { useSmartSkeleton } from '@/hooks/useSmartSkeleton';
import { cn } from '@/lib/utils';
import { Separator } from '../ui/separator';
import { AppLogo } from './AppLogo';
import { NavBadgeDisplay } from './NavBadgeDisplay';

export function Sidebar() {
  const { name, navItems } = useApp();
  const { user, logout, isLoading } = useAuth();
  const { checkPermission } = usePermissions();
  const { collapsed, toggle } = useSidebar();
  const navigate = useNavigate();
  const showUserSkeleton = useSmartSkeleton(isLoading);

  const handleLogout = async () => {
    await logout();
    navigate('/login?reason=logout');
  };

  const hasNavItemAccess = (navItem: any) => {
    if (!navItem.requiredPermissions || navItem.requiredPermissions.length === 0) return true;
    return navItem.requiredPermissions.every((permission: any) => checkPermission(permission));
  };

  // ── Collapsed sidebar (icon rail) ────────────────────────────────────────
  if (collapsed) {
    return (
      <div className="flex h-full flex-col border-r bg-muted/40">
        {/* Header — logo + expand toggle, same height as expanded header */}
        <div className="flex h-14 flex-col items-center justify-center border-b lg:h-[60px]">
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={toggle}
                className="flex items-center justify-center rounded-lg p-2 text-muted-foreground hover:bg-muted hover:text-primary transition-colors"
              >
                <PanelLeftOpen className="h-5 w-5" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={8}>
              Expand sidebar (⌘B)
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Nav icons */}
        <nav className="flex flex-1 flex-col items-center gap-1 overflow-y-auto py-3">
          {navItems
            .filter((section) => section.links.some(hasNavItemAccess))
            .flatMap((section) =>
              section.links.filter(hasNavItemAccess).map((link) => (
                <Tooltip key={link.href} delayDuration={0}>
                  <TooltipTrigger>
                    <NavLink
                      to={link.href}
                      end={link.end}
                      className={({ isActive }) =>
                        cn(
                          'relative grid h-9 w-9 place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-primary',
                          isActive && 'bg-muted text-primary'
                        )
                      }
                    >
                      <link.icon className="h-5 w-5" />
                      {link.badge && <CollapsedBadgeDot badge={link.badge} />}
                    </NavLink>
                  </TooltipTrigger>
                  <TooltipContent side="right" sideOffset={8} className="text-xs">
                    {link.label}
                  </TooltipContent>
                </Tooltip>
              ))
            )}
        </nav>

        {/* Bottom: user avatar + settings + logout */}
        <div className="flex flex-col items-center gap-0.5 border-t py-2">
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Link
                to="/"
                className="grid h-8 w-8 place-items-center rounded-full bg-muted-foreground/20 text-muted-foreground hover:text-primary transition-colors"
              >
                <UserIcon className="h-4 w-4" />
              </Link>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={8} className="text-xs">
              {user?.name ?? 'Profile'}
            </TooltipContent>
          </Tooltip>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <Link
                to="/profile"
                className="flex h-10 w-10 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-primary"
              >
                <Settings className="h-[18px] w-[18px]" />
              </Link>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={8} className="text-xs">
              Settings
            </TooltipContent>
          </Tooltip>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={handleLogout}
                className="flex h-10 w-10 items-center justify-center rounded-lg text-destructive transition-colors hover:bg-destructive/10"
              >
                <LogOut className="h-[18px] w-[18px]" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" sideOffset={8} className="text-xs">
              Logout
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    );
  }

  // ── Expanded sidebar ─────────────────────────────────────────────────────

  const navLinkClasses = ({ isActive }: { isActive: boolean }) =>
    cn(
      'flex items-center gap-3 rounded-lg px-3 py-2 text-muted-foreground transition-colors hover:text-primary',
      isActive && 'bg-muted text-primary'
    );

  return (
    <div className="h-full border-r bg-muted/40 overflow-hidden">
      <div className="flex h-full flex-col gap-2">
        <div className="flex h-14 items-center border-b px-4 lg:h-[60px] lg:px-6">
          <NavLink to="/" className="flex items-center gap-2 font-semibold">
            <AppLogo className="h-6 w-6 text-primary" />
            <span>{name}</span>
          </NavLink>
          <Button
            variant="ghost"
            size="icon"
            className="ml-auto h-7 w-7"
            onClick={toggle}
            title="Collapse sidebar (⌘B)"
          >
            <PanelLeftClose className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex-1 overflow-y-auto">
          <nav className="grid items-start gap-1 p-2 text-sm font-medium lg:p-4">
            {navItems
              .filter((section) => section.links.some(hasNavItemAccess))
              .map((section, sectionIndex) => (
                <div key={section.title || sectionIndex} className="py-2">
                  {section.title && (
                    <h3 className="mb-1 rounded-lg px-3 text-xs font-semibold uppercase text-muted-foreground">
                      {section.title}
                    </h3>
                  )}
                  {section.links.filter(hasNavItemAccess).map((link) => (
                    <NavLink
                      key={link.href}
                      to={link.href}
                      className={navLinkClasses}
                      end={link.end}
                    >
                      <link.icon className="h-4 w-4" />
                      {link.label}
                      {link.badge && <NavBadgeDisplay badge={link.badge} />}
                    </NavLink>
                  ))}
                </div>
              ))}
          </nav>
        </div>
        <div className="mt-auto flex flex-col p-4">
          <Separator className="mb-4" />
          <div className="flex flex-col gap-1">
            {showUserSkeleton ? (
              <div className="flex items-center gap-3 px-3 py-1">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="flex flex-col gap-1 min-w-0 flex-1">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-3 w-32" />
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 px-3 py-1">
                <div className="grid h-8 w-8 place-items-center rounded-full bg-muted-foreground/20">
                  <UserIcon className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="font-semibold truncate">{user?.name}</span>
                  <span className="text-xs text-muted-foreground truncate">{user?.email}</span>
                </div>
              </div>
            )}
            <nav className="grid items-start text-sm font-medium">
              <Link
                to="/profile"
                className={cn(navLinkClasses({ isActive: false }), 'w-full py-1.5')}
              >
                <Settings className="h-4 w-4" />
                Account Settings
              </Link>
              <Button
                variant="ghost"
                onClick={handleLogout}
                className={cn(
                  navLinkClasses({ isActive: false }),
                  'w-full justify-start py-1.5 text-destructive hover:text-destructive'
                )}
              >
                <LogOut className="h-4 w-4" />
                Logout
              </Button>
            </nav>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Small dot indicator for collapsed sidebar icons when badge count > 0. */
function CollapsedBadgeDot({ badge }: { badge: NavBadge }) {
  const { count } = badge.useCount();
  if (count <= 0) return null;
  return <span className="absolute top-0.5 right-0.5 size-2 rounded-full bg-primary" />;
}
